import { createSlice } from '../../utils/createAppSlice'
import { userInitialState as initialState } from './userInitialState'
import {
	resetStripeCustomerData,
	saveMetrics,
	saveOnboardingData,
	saveStripeCustomerData,
	saveUserData,
	updateStatus,
} from './userReducers'
import {
	getCreateUserStatus,
	getOnboardingData,
	getStripeCustomerData,
	getUserData,
	getUserMetrics,
} from './userSelectors'
import { UserState } from './types'
import { Status } from '../../types'
import {
	createAppUser,
	createCrmContact,
	createStripeCustomerSession,
	editUserData,
	fetchUserData,
	fetchUserMetrics,
} from './userAsyncMethods'

export const userSlice = createSlice({
	name: 'user',
	initialState,
	reducers: (create) => ({
		actionResetUserState: create.reducer(() => initialState),
		actionSaveOnboardingData: create.reducer(saveOnboardingData),
		actionCreateUser: create.asyncThunk(createAppUser, {
			pending: (state: UserState) => updateStatus(state.userData, Status.PENDING),
			rejected: (state: UserState) => updateStatus(state.userData, Status.REJECTED),
			fulfilled: (state: UserState) => updateStatus(state.userData, Status.IDLE),
		}),
		actionFetchUserData: create.asyncThunk(fetchUserData, {
			pending: (state: UserState) => updateStatus(state.userData, Status.PENDING),
			rejected: (state: UserState) => updateStatus(state.userData, Status.REJECTED),
			fulfilled: saveUserData,
		}),
		actionFetchMetrics: create.asyncThunk(fetchUserMetrics, {
			pending: (state: UserState) => updateStatus(state.metrics, Status.PENDING),
			rejected: (state: UserState) => updateStatus(state.metrics, Status.REJECTED),
			fulfilled: saveMetrics,
		}),
		actionEditUserData: create.asyncThunk(editUserData, { fulfilled: saveUserData }),
		actionUpdateUserData: create.asyncThunk(fetchUserData, { fulfilled: saveUserData }),
		actionCreateStripeCheckoutSession: create.asyncThunk(createStripeCustomerSession, {
			pending: (state: UserState) => updateStatus(state.stripeCustomerData, Status.PENDING),
			rejected: (state: UserState) => updateStatus(state.stripeCustomerData, Status.REJECTED),
			fulfilled: saveStripeCustomerData,
		}),
		actionCreateCrmContact: create.asyncThunk(createCrmContact, { fulfilled: saveStripeCustomerData }),
		actionResetStripeCustomerData: create.reducer(resetStripeCustomerData),
	}),
	selectors: {
		selectOnboardingData: getOnboardingData,
		selectCreateUserStatus: getCreateUserStatus,
		selectUserData: getUserData,
		selectUserMetrics: getUserMetrics,
		selectStripeCustomerData: getStripeCustomerData,
	},
})

export const {
	actionSaveOnboardingData,
	actionCreateUser,
	actionFetchUserData,
	actionResetUserState,
	actionFetchMetrics,
	actionEditUserData,
	actionUpdateUserData,
	actionCreateStripeCheckoutSession,
	actionCreateCrmContact,
	actionResetStripeCustomerData,
} = userSlice.actions

export const {
	selectOnboardingData,
	selectCreateUserStatus,
	selectUserData,
	selectUserMetrics,
	selectStripeCustomerData,
} = userSlice.selectors
